// أدوات مشتركة لصفحات المشاريع (وربط المشروع بالتذكرة).

export const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

export const getTenantLabel = (tenant) => tenant?.tenantName || tenant?.username || tenant?._id || '';

// العملاء اللي يقدر المستخدم يشتغل عليهم — نفس منطق صفحة التذاكر.
export const getScopedTenants = (tenants, user) => {
    const list = Array.isArray(tenants) ? tenants : [];
    if (user?.role === 'admin') return list;
    if (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportSuperAdminEnabled)) return list;
    if (!isTruthyFlag(user?.supportAdminEnabled) || !isTruthyFlag(user?.supportTenantAdminEnabled)) return list;

    const scope = String(user?.supportTenantScope || 'own');
    if (scope === 'all') return list;
    if (scope === 'selected') {
        const allowedIds = Array.isArray(user?.supportTenantIds) ? user.supportTenantIds.map(String) : [];
        return list.filter((tenant) => allowedIds.includes(String(tenant?._id)));
    }
    const ownTenantId = user?.tenant_id?._id || user?.tenant_id || null;
    if (!ownTenantId) return [];
    return list.filter((tenant) => String(tenant?._id) === String(ownTenantId));
};

// بيانات العميل اللي بتتحفظ مع التذكرة / المشروع من سجل الـ tenant.
export const buildTenantCustomerFields = (tenant) => {
    if (!tenant) return {};
    const company = tenant?.companies?.[0];
    const branch = company?.branches?.[0];
    const owner = tenant?.ownerUser || null;
    return {
        tenantId: tenant?._id || null,
        tenantName: getTenantLabel(tenant),
        customerName: owner?.name || tenant?.contactPersonName || getTenantLabel(tenant) || '',
        customerId: owner?._id || tenant?.userId || tenant?._id || '',
        email: owner?.email || company?.mainEmail || '',
        mobile: owner?.mobile || tenant?.contactPersonMobile || tenant?.tenantPhone || company?.mainPhone || '',
        companyId: company?._id || null,
        branchId: branch?._id || null
    };
};

export const toDatetimeLocalValue = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    const pad = (num) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const toIsoFromDatetimeLocal = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const [datePart, timePart] = raw.split('T');
    if (!datePart || !timePart) return null;
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm] = timePart.split(':').map(Number);
    if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
    const date = new Date(y, m - 1, d, hh, mm, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

// Project dates are picked as dates (a datetime field gives an empty value until its time is filled
// too, which saved the project with no dates). "2026-10-01" <-> the date at local midnight; the
// expected (end) date is kept at the end of that day so the whole day counts.
export const toDateInputValue = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    const pad = (num) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const toIsoFromDateInput = (value, endOfDay = false) => {
    const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const date = endOfDay
        ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 0, 0)
        : new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 0, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

// Project status (older projects without one are running).
export const PROJECT_STATUSES = [
    { value: 'running', ar: 'جاري', en: 'Running', bg: '#dcfce7', color: '#15803d', dot: '#22c55e' },
    { value: 'suspended', ar: 'متوقف', en: 'Suspended', bg: '#fef3c7', color: '#b45309', dot: '#f59e0b' },
    { value: 'completed', ar: 'مكتمل', en: 'Completed', bg: '#ccfbf1', color: '#0f766e', dot: '#14b8a6' },
    { value: 'canceled', ar: 'ملغي', en: 'Canceled', bg: '#fee2e2', color: '#b91c1c', dot: '#ef4444' },
];
export const getProjectStatus = (status) =>
    PROJECT_STATUSES.find((item) => item.value === String(status || '').toLowerCase()) || PROJECT_STATUSES[0];

export const formatDateTime = (raw) => {
    const date = raw ? new Date(raw) : null;
    if (!date || Number.isNaN(date.getTime())) return '-';
    const pad = (num) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export { getAttachmentInfo } from './attachmentNames';

export const projectOptionLabel = (project) =>
    project ? `#${project.projectNumber ?? '-'} — ${project.title || ''}` : '';

export const sprintOptionLabel = (sprint, lang = 'ar') => {
    if (!sprint) return '';
    const prefix = `${lang === 'ar' ? 'دورة' : 'Sprint'} ${sprint.sprintNumber ?? '-'}`;
    return sprint.title ? `${prefix} — ${sprint.title}` : prefix;
};

export const formatDate = (raw) => {
    const date = raw ? new Date(raw) : null;
    if (!date || Number.isNaN(date.getTime())) return '-';
    const pad = (num) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// مرحلة الدورة من تواريخها وتقدّم تذاكرها: مكتملة / متأخرة / جارية / مخططة / بدون تواريخ.
export const getSprintPhase = (sprint, doneCount = 0, nowMs = Date.now()) => {
    const total = Number(sprint?.ticketsCount || 0);
    const start = sprint?.startAt ? new Date(sprint.startAt).getTime() : null;
    const end = sprint?.endAt ? new Date(sprint.endAt).getTime() : null;
    if (total > 0 && doneCount >= total) return 'done';
    if (end && nowMs > end) return 'overdue';
    if (start && nowMs < start) return 'planned';
    if (start || end) return 'active';
    return 'unscheduled';
};

export const SPRINT_PHASE_META = {
    done: { ar: 'مكتملة', en: 'Completed', bg: '#ccfbf1', color: '#0f766e' },
    overdue: { ar: 'متأخرة', en: 'Overdue', bg: '#fee2e2', color: '#b91c1c' },
    active: { ar: 'جارية', en: 'Active', bg: '#dcfce7', color: '#15803d' },
    planned: { ar: 'مخططة', en: 'Planned', bg: '#dbeafe', color: '#1d4ed8' },
    unscheduled: { ar: 'بدون تواريخ', en: 'Unscheduled', bg: '#f1f5f9', color: '#475569' },
};
